/**
 * HorseCommsSection — Communications card + all comm dialogs for HorseOverview.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Skeleton } from '../../ui/skeleton';
import { MessageSquare } from 'lucide-react';
import { SectionHeader } from '../../common/OverviewHelpers';
import { CommunicationDialog, CommunicationDetailDialog } from '../../crm/ActivityDialogs';

const SectionSkeleton = ({ rows = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
  </div>
);

const HorseCommsSection = ({
  horse,
  communications = [],
  commsLoading,
  invalidateComms,
  tasks = [],
  tenantUsers = [],
  accounts = [],
}) => {
  const [open, setOpen] = useState(false);
  const [searchLocal, setSearchLocal] = useState('');
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commDetailOpen, setCommDetailOpen] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [allCommsDialogOpen, setAllCommsDialogOpen] = useState(false);
  const [commSearchTerm, setCommSearchTerm] = useState('');
  const [commMethodFilter, setCommMethodFilter] = useState('all');

  const matchComm = (comm, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return comm.activity_name?.toLowerCase().includes(s) ||
           comm.description?.toLowerCase().includes(s) ||
           comm.communication_method?.toLowerCase().includes(s);
  };

  const filtered = communications.filter(c => matchComm(c, searchLocal));
  const filteredAll = communications.filter(c => {
    const searchMatch = commSearchTerm === '' || matchComm(c, commSearchTerm);
    const methodMatch = commMethodFilter === 'all' || c.communication_method === commMethodFilter;
    return searchMatch && methodMatch;
  });

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <Collapsible open={open} onOpenChange={setOpen}>
            <SectionHeader
              title="Communications"
              icon={MessageSquare}
              isOpen={open}
              onToggle={() => setOpen(o => !o)}
              action={() => setCommDialogOpen(true)}
            />
            <CollapsibleContent className="mt-4">
              <div className="mb-3">
                <Input
                  placeholder="Search communications..."
                  value={searchLocal}
                  onChange={e => setSearchLocal(e.target.value)}
                  className="w-full"
                />
              </div>
              {commsLoading && <SectionSkeleton rows={3} />}
              {!commsLoading && (
                filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No communications found.</p>
                ) : (
                  <div className="space-y-2">
                    {filtered.slice(0, 5).map(comm => (
                      <div
                        key={comm.id}
                        className="p-3 border rounded hover:bg-muted/50 cursor-pointer"
                        onClick={() => { setSelectedComm(comm); setCommDetailOpen(true); }}
                        data-testid={`comm-${comm.id}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-medium text-sm">{comm.activity_name}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {comm.communication_method?.replace('_', ' ')}
                          </Badge>
                        </div>
                        {comm.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{comm.description}</p>}
                        {comm.start_date_time && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(comm.start_date_time).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}
              <div className="pt-3 border-t mt-3">
                <Button variant="outline" size="sm" className="w-full" onClick={() => setAllCommsDialogOpen(true)}>
                  View All Communications ({communications.length})
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Communication Creation Dialog */}
      <CommunicationDialog
        open={commDialogOpen}
        onOpenChange={setCommDialogOpen}
        accountId={null}
        horseId={horse.id}
        onSuccess={() => { setCommDialogOpen(false); invalidateComms?.(); }}
        associations={[]}
        users={tenantUsers}
        contactEntries={[]}
        tasks={tasks}
        accounts={accounts}
        showAccountDropdown={true}
      />

      {/* Communication Detail Dialog */}
      <CommunicationDetailDialog
        open={commDetailOpen}
        onOpenChange={setCommDetailOpen}
        communication={selectedComm}
        onSuccess={invalidateComms}
        onCommunicationUpdate={updated => { setSelectedComm(updated); invalidateComms?.(); }}
        canEditAccount={true}
        users={tenantUsers}
        showAccountDropdown={true}
        accounts={accounts}
        associations={[]}
      />

      {/* View All Communications Dialog */}
      <Dialog open={allCommsDialogOpen} onOpenChange={setAllCommsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>All Communications</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search communications..."
              value={commSearchTerm}
              onChange={e => setCommSearchTerm(e.target.value)}
              className="w-full"
            />
            <div>
              <Label className="text-xs text-muted-foreground mb-1">Method</Label>
              <Select value={commMethodFilter} onValueChange={setCommMethodFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="telephone">Telephone</SelectItem>
                  <SelectItem value="email_direct">Email (Direct)</SelectItem>
                  <SelectItem value="email_newsletter">Email (Newsletter)</SelectItem>
                  <SelectItem value="video_conference">Video Conference</SelectItem>
                  <SelectItem value="text">Text Message</SelectItem>
                  <SelectItem value="mailer">Mailer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {filteredAll.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No communications found</p>
            ) : filteredAll.map(comm => (
              <div
                key={comm.id}
                className="p-3 border rounded hover:bg-muted/50 cursor-pointer"
                onClick={() => { setSelectedComm(comm); setCommDetailOpen(true); setAllCommsDialogOpen(false); }}
              >
                <div className="flex justify-between items-start mb-1">
                  <p className="font-medium text-sm">{comm.activity_name}</p>
                  <Badge variant="outline" className="text-xs capitalize">
                    {comm.communication_method?.replace('_', ' ')}
                  </Badge>
                </div>
                {comm.description && <p className="text-xs text-muted-foreground mt-1">{comm.description}</p>}
                {comm.start_date_time && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(comm.start_date_time).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HorseCommsSection;
